import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { Card } from '../components/Card';
import { btnPrimary } from '../components/buttons';

const SEMVER = /^\d+\.\d+\.\d+$/;

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600 as const,
  color: 'var(--ink-3)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--line-strong)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontSize: 14,
  fontFamily: 'inherit',
};

export function AppVersionScreen() {
  const qc = useQueryClient();
  const { data, isFetching, error } = useQuery({
    queryKey: ['app-version'],
    queryFn: () => adminApi.appVersion(),
  });

  const [minVersion, setMinVersion] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setMinVersion(data.minVersion);
      setLatestVersion(data.latestVersion);
      setMessage(data.message ?? '');
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.updateAppVersion({
        minVersion: minVersion.trim(),
        latestVersion: latestVersion.trim(),
        message: message.trim() ? message.trim() : null,
      }),
    onSuccess: (fresh) => {
      qc.setQueryData(['app-version'], fresh);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const minValid = SEMVER.test(minVersion.trim());
  const latestValid = SEMVER.test(latestVersion.trim());
  const canSave = minValid && latestValid && !mutation.isPending;

  if (error) {
    return (
      <div style={{ padding: '24px 28px', color: 'var(--danger, #e5484d)' }}>
        Failed to load app version config: {(error as Error).message}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 640 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>App Version</h1>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 20px', lineHeight: 1.6 }}>
        Controls the iOS update gate. Clients below <b>Min version</b> get a <b>forced</b> (non-dismissable)
        update screen; clients at or above min but below <b>Latest version</b> get a <b>recommended</b>
        (dismissable) update toast. Set both to <code>0.0.0</code> to gate nobody. Changes take effect within ~60s.
      </p>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {isFetching && !data ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
        ) : (
          <>
            <div>
              <label style={labelStyle}>Min iOS version (below = forced update)</label>
              <input
                style={{ ...inputStyle, borderColor: minValid ? 'var(--line-strong)' : 'var(--danger, #e5484d)' }}
                value={minVersion}
                onChange={(e) => setMinVersion(e.target.value)}
                placeholder="2.1.0"
                inputMode="decimal"
              />
            </div>

            <div>
              <label style={labelStyle}>Latest iOS version (below = recommended update)</label>
              <input
                style={{ ...inputStyle, borderColor: latestValid ? 'var(--line-strong)' : 'var(--danger, #e5484d)' }}
                value={latestVersion}
                onChange={(e) => setLatestVersion(e.target.value)}
                placeholder="2.3.0"
                inputMode="decimal"
              />
            </div>

            <div>
              <label style={labelStyle}>Update message (optional)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A new version is available with improvements and fixes."
              />
            </div>

            {!minValid || !latestValid ? (
              <div style={{ fontSize: 12, color: 'var(--danger, #e5484d)' }}>
                Versions must be semantic <code>x.y.z</code> (e.g. 2.1.0).
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                type="button"
                style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed', border: 'none' }}
                disabled={!canSave}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </button>
              {saved && <span style={{ fontSize: 13, color: 'var(--brand-ink, #16a34a)' }}>Saved ✓</span>}
              {mutation.isError && (
                <span style={{ fontSize: 13, color: 'var(--danger, #e5484d)' }}>
                  {(mutation.error as Error).message}
                </span>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
